import {
  createMatterNoteV1,
  deleteMatterNoteV1,
  retrieveMatterNotesV2,
  updateMatterNoteV1,
} from "@/lib/data/requests";
import { invalidateAnnotationQueries } from "@/lib/data/react-query/annotation-query-utils";
import { queryKeys } from "@/lib/data/static";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveMatterNotesQueryOptions = (
  data: Parameters<typeof retrieveMatterNotesV2>[0],
) => ({
  queryKey: [
    queryKeys.matters,
    data.matterId,
    "notes",
    data.pageNumber ?? 1,
    data.pageSize ?? 1000,
  ],
  enabled: Boolean(data.matterId),
  queryFn: () => retrieveMatterNotesV2(data),
});

const invalidateMatterNotes = (queryClient: QueryClient, matterId?: string) => {
  if (!matterId) {
    return Promise.resolve();
  }

  return queryClient.invalidateQueries({
    queryKey: [queryKeys.matters, matterId, "notes"],
  });
};

export const useCreateMatterNote = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterNoteV1>[0]) =>
      createMatterNoteV1(data),
    onError: () => {
      toast.error("Error creating matter note");
    },
    onSettled: (_data, _error, variables) => {
      void invalidateMatterNotes(queryClient, variables.matterId);
      if (!invalidateOnSettled) {
        return;
      }
      void invalidateAnnotationQueries(queryClient, variables);
    },
    onSuccess: (_data, variables) => {
      if (!showSuccessToast) {
        return;
      }
      if (variables.draft) {
        toast.success("Note saved as draft");
        return;
      }
      toast.success("Matter note created successfully");
    },
  });
};

export const useUpdateMatterNote = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterNoteV1>[0]) =>
      updateMatterNoteV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter note");
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateMatterNotes(queryClient, variables.matterId);
      if (invalidateOnSettled) {
        await invalidateAnnotationQueries(queryClient, variables);
      }
    },
    onSuccess: (_data, variables) => {
      if (!showSuccessToast) {
        return;
      }
      if (variables.draft) {
        toast.success("Note saved as draft");
        return;
      }
      toast.success("Matter note updated successfully");
    },
  });
};

export const useDeleteMatterNote = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterNoteV1>[0]) =>
      deleteMatterNoteV1(data),
    onError: () => {
      toast.error("Error deleting matter note");
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateMatterNotes(queryClient, variables.matterId);
      if (invalidateOnSettled) {
        await invalidateAnnotationQueries(queryClient, variables);
      }
    },
    onSuccess: () => {
      if (!showSuccessToast) {
        return;
      }
      toast.success("Matter note deleted successfully");
    },
  });
};
